/**
 * JsonPropertyFields - Decompose a JSON property into individual CRM fields.
 *
 * When a property has `field_type_name: "json"` and a `schema_property` linking
 * it to a companion JSON Schema, this component expands the JSON object into
 * individual fields using the schema's `x-entity-type` and `x-field-type`
 * annotations.
 *
 * Entity references (`x-entity-type`) render as association selects:
 * - Single string → single-select entity picker
 * - Array of strings → multi-select entity picker
 *
 * Field type references (`x-field-type`) render as standard PropertyInputs:
 * - Maps to the same input component used for that field type on regular properties
 *
 * This is a generic system — any entity type with a JSON + JSON Schema pair
 * gets decomposed fields automatically. No hardcoded entity type names.
 */

import { Link03 } from '@untitledui/icons';
import React, { useCallback, useMemo } from 'react';

import { EntityBadgeByIds } from '@/lib/crm/components/badges';

import type { AssociationType } from '../../core/SchemaClient';
import type { JsonSchemaField } from '../../hooks/useJsonSchemaFields';
import { useJsonSchemaFields } from '../../hooks/useJsonSchemaFields';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import { PropertyDiffValue } from '../../record/timeline/components/shared/PropertyDiffValue';
import { getPropertyTypeIconComponent } from '../../utils/icons/PropertyTypeIcon';
import { AssociatedCrmObjectInput } from '../associations/AssociatedCrmObjectInput';
import { ExpandablePropertyValue } from './labels/ExpandablePropertyValue';
import { PropertyInput } from './PropertyInput';

/**
 * Whether a decomposed sub-field differs between two config objects.
 *
 * Compared by serialised shape rather than reference: sub-values are frequently
 * arrays (a multi-select of property types, a list of entity ids), and two
 * arrays with identical contents are never reference-equal, so a `!==` check
 * would mark every sub-field as changed the moment any one of them did.
 */
function subFieldChanged(
  field: JsonSchemaField,
  config: Record<string, unknown> | null,
  previousConfig: Record<string, unknown> | null
): boolean {
  if (!previousConfig) {
    return false;
  }
  const next = config?.[field.key] ?? null;
  const prior = previousConfig[field.key] ?? null;
  return JSON.stringify(next) !== JSON.stringify(prior);
}

// =============================================================================
// Synthetic type factories
// =============================================================================

/**
 * Create a synthetic AssociationType for entity refs stored in JSON properties.
 * AssociatedCrmObjectInput uses target_entity_type_name for search and
 * cardinality for single vs multi-select behavior.
 */
function createSyntheticAssociationType(
  field: JsonSchemaField,
  sourceEntityTypeName: string
): AssociationType {
  return {
    association_type_name: `__json_${field.key}`,
    label: field.label,
    source_entity_type_name: sourceEntityTypeName,
    target_entity_type_name: field.entityTypeName ?? '',
    reverse_association_type_name: '',
    cardinality: field.isMulti ? 'many_to_many' : 'many_to_one',
    value_read_access: 'all',
    value_write_access: 'all',
    transitive_via: null,
  };
}

/**
 * Create a synthetic PropertyTypeSchema for field-type refs stored in JSON properties.
 * PropertyInput uses field_type_name to select the right input component.
 */
function createSyntheticPropertyType(
  field: JsonSchemaField,
  entityTypeName: string
): PropertyTypeSchema {
  return {
    property_type_name: `__json_${field.key}`,
    entity_type_name: entityTypeName,
    label: field.label,
    field_type_name: field.fieldTypeName ?? 'text',
    description: field.description ?? '',
    is_required: field.isRequired,
    is_unique_value: false,
    is_multi: field.isMulti,
    value_write_access: 'all',
    value_read_access: 'all',
    value_update_access: 'all',
    // Boolean fields need explicit options since there's no backend property
    // type to provide them — SelectInput renders an empty dropdown without these.
    ...(field.fieldTypeName === 'boolean'
      ? {
          options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ],
        }
      : {}),
  };
}

// =============================================================================
// Value conversion helpers
// =============================================================================

/**
 * Extract entity IDs from a JSON config value for a given field.
 * Normalizes to string[] for AssociatedCrmObjectInput.
 */
function getEntityIdsFromConfig(
  config: Record<string, unknown> | null,
  field: JsonSchemaField
): string[] | null {
  if (!config) {
    return null;
  }
  const raw = config[field.key];
  if (raw === null || raw === undefined) {
    return null;
  }
  if (field.isMulti) {
    return Array.isArray(raw) ? (raw as string[]) : null;
  }
  return typeof raw === 'string' ? [raw] : null;
}

/**
 * Extract a scalar value from a JSON config for a field-type field.
 */
function getFieldValueFromConfig(
  config: Record<string, unknown> | null,
  field: JsonSchemaField
): unknown {
  if (!config) {
    return null;
  }
  const raw = config[field.key];
  return raw ?? null;
}

// =============================================================================
// Read-mode components
// =============================================================================

interface EntityRefReadRowProps {
  field: JsonSchemaField;
  config: Record<string, unknown> | null;
  /** Prior config when a draft proposes a change; null on live records. */
  previousConfig: Record<string, unknown> | null;
  orgId: number;
}

/**
 * Read-only entity reference row.
 * Extracts entity IDs from the JSON config and hands them to EntityBadgeByIds,
 * which performs the store hydration / fetch. Going through useAssociations
 * here would just round-trip the same ids through a redundant resolution layer.
 */
function EntityRefReadRow({
  field,
  config,
  previousConfig,
  orgId,
}: EntityRefReadRowProps) {
  const entityIds = useMemo(
    () => getEntityIdsFromConfig(config, field) ?? [],
    [config, field]
  );
  const changed = subFieldChanged(field, config, previousConfig);
  const priorEntityIds = useMemo(
    () =>
      changed ? (getEntityIdsFromConfig(previousConfig, field) ?? []) : [],
    [changed, previousConfig, field]
  );

  return (
    <div className='flex items-start gap-3'>
      <Link03
        className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5'
        aria-hidden='true'
      />
      <span className='text-xs md:text-sm text-fg-quaternary w-40 flex-shrink-0'>
        {field.label}
      </span>
      {/* Badges can't be struck through legibly, so a changed list shows the
          prior badges dimmed ahead of an arrow rather than with a line through
          them. Unchanged rows render exactly as they always have. */}
      <div className='flex flex-wrap items-center gap-1 min-w-0'>
        {changed && priorEntityIds.length > 0 && (
          <>
            <span className='opacity-50'>
              <EntityBadgeByIds
                entityIds={priorEntityIds}
                orgId={orgId}
                variant='xs'
              />
            </span>
            <span className='text-tertiary' aria-hidden='true'>
              →
            </span>
          </>
        )}
        <EntityBadgeByIds entityIds={entityIds} orgId={orgId} variant='xs' />
      </div>
    </div>
  );
}

interface FieldTypeReadRowProps {
  field: JsonSchemaField;
  config: Record<string, unknown> | null;
  /** Prior config when a draft proposes a change; null on live records. */
  previousConfig: Record<string, unknown> | null;
  entityTypeName: string;
  orgId: number;
}

function FieldTypeReadRow({
  field,
  config,
  previousConfig,
  entityTypeName,
  orgId,
}: FieldTypeReadRowProps) {
  const value = getFieldValueFromConfig(config, field);
  const syntheticPropType = useMemo(
    () => createSyntheticPropertyType(field, entityTypeName),
    [field, entityTypeName]
  );
  const IconComponent = getPropertyTypeIconComponent(syntheticPropType);
  const changed = subFieldChanged(field, config, previousConfig);

  return (
    <div className='flex items-start gap-3'>
      <IconComponent
        className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5'
        aria-hidden='true'
      />
      <span className='text-xs md:text-sm text-fg-quaternary w-40 flex-shrink-0'>
        {field.label}
      </span>
      <div className='text-xs md:text-sm font-normal text-fg-primary min-w-0'>
        {changed ? (
          // Same renderer the record timeline and the top-level overview rows
          // use, so a changed sub-field gets the identical old → new treatment
          // rather than a second dialect of "this changed".
          <PropertyDiffValue
            oldValue={getFieldValueFromConfig(previousConfig, field)}
            newValue={value}
            schema={syntheticPropType}
            orgId={orgId}
          />
        ) : (
          <ExpandablePropertyValue
            value={value}
            propertyType={syntheticPropType}
            orgId={orgId}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Edit-mode components
// =============================================================================

interface EntityRefEditRowProps {
  field: JsonSchemaField;
  config: Record<string, unknown> | null;
  orgId: number;
  sourceEntityTypeName: string;
  sourceEntityId: string;
  onFieldChange: (key: string, value: unknown) => void;
}

function EntityRefEditRow({
  field,
  config,
  orgId,
  sourceEntityTypeName,
  sourceEntityId,
  onFieldChange,
}: EntityRefEditRowProps) {
  const entityIds = getEntityIdsFromConfig(config, field);
  const syntheticAssocType = useMemo(
    () => createSyntheticAssociationType(field, sourceEntityTypeName),
    [field, sourceEntityTypeName]
  );

  const handleChange = useCallback(
    (newValue: string[] | null) => {
      if (field.isMulti) {
        // Array field → store as array
        onFieldChange(field.key, newValue ?? []);
      } else {
        // Single field → store as string or null
        onFieldChange(
          field.key,
          newValue && newValue.length > 0 ? newValue[0] : null
        );
      }
    },
    [field.key, field.isMulti, onFieldChange]
  );

  return (
    <div className='flex items-start gap-2'>
      <Link03
        className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5'
        aria-hidden='true'
      />
      <div className='flex-1 min-w-0'>
        <div className='text-xs md:text-sm text-fg-quaternary mb-1'>
          {field.label}
        </div>
        <AssociatedCrmObjectInput
          associationType={syntheticAssocType}
          value={entityIds}
          onChange={handleChange}
          orgId={orgId}
          sourceEntityId={sourceEntityId}
          hideCreateOption
        />
      </div>
    </div>
  );
}

interface FieldTypeEditRowProps {
  field: JsonSchemaField;
  config: Record<string, unknown> | null;
  entityTypeName: string;
  onFieldChange: (key: string, value: unknown) => void;
}

function FieldTypeEditRow({
  field,
  config,
  entityTypeName,
  onFieldChange,
}: FieldTypeEditRowProps) {
  const value = getFieldValueFromConfig(config, field);
  const syntheticPropType = useMemo(
    () => createSyntheticPropertyType(field, entityTypeName),
    [field, entityTypeName]
  );
  const IconComponent = getPropertyTypeIconComponent(syntheticPropType);

  const handleChange = useCallback(
    (newValue: unknown) => {
      onFieldChange(field.key, newValue);
    },
    [field.key, onFieldChange]
  );

  return (
    <div className='flex items-start gap-2'>
      <IconComponent
        className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5'
        aria-hidden='true'
      />
      <div className='flex-1 min-w-0'>
        <div className='text-xs md:text-sm text-fg-quaternary mb-1'>
          {syntheticPropType.label}
        </div>
        <PropertyInput
          propertyType={syntheticPropType}
          value={value as string | number | object | null | undefined}
          onChange={handleChange}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Container components
// =============================================================================

export interface JsonPropertyReadFieldsProps {
  /** The companion JSON Schema defining the config structure */
  schema: Record<string, unknown> | null;
  /** Current config value (the JSON object) */
  value: Record<string, unknown> | null;
  /**
   * The config as it stands before a pending proposal is applied. Supplied only
   * when a draft changes this property; sub-fields that differ from it render
   * `old → new`, and every other row is untouched. Omitted on live records.
   */
  previousValue?: Record<string, unknown> | null;
  /** Organization ID */
  orgId: number;
  /** Source entity type name (e.g. "ahoy_event_automation") */
  sourceEntityTypeName: string;
}

/**
 * Renders decomposed JSON property fields in read-only mode.
 * Used in OverviewSection to replace the raw JSON display with individual field rows.
 */
export function JsonPropertyReadFields({
  schema,
  value,
  previousValue = null,
  orgId,
  sourceEntityTypeName,
}: JsonPropertyReadFieldsProps) {
  const fields = useJsonSchemaFields(schema);

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      {fields.map(field => {
        if (field.refType === 'entity') {
          return (
            <EntityRefReadRow
              key={field.key}
              field={field}
              config={value}
              previousConfig={previousValue}
              orgId={orgId}
            />
          );
        }
        return (
          <FieldTypeReadRow
            key={field.key}
            field={field}
            config={value}
            previousConfig={previousValue}
            entityTypeName={sourceEntityTypeName}
            orgId={orgId}
          />
        );
      })}
    </>
  );
}

export interface JsonPropertyEditFieldsProps {
  /** The companion JSON Schema defining the config structure */
  schema: Record<string, unknown> | null;
  /** Current config value (the JSON object) */
  value: Record<string, unknown> | null;
  /** Organization ID */
  orgId: number;
  /** Source entity type name (e.g. "ahoy_event_automation") */
  sourceEntityTypeName: string;
  /** Source entity ID for association context */
  sourceEntityId: string;
  /** Called when any field value changes, receives the full updated config object */
  onChange: (updatedConfig: Record<string, unknown>) => void;
}

/**
 * Renders decomposed JSON property fields in edit mode.
 * Used in OverviewEditSection to replace a single JSON input with individual field inputs.
 * When any field changes, the full updated JSON config is emitted via onChange.
 */
export function JsonPropertyEditFields({
  schema,
  value,
  orgId,
  sourceEntityTypeName,
  sourceEntityId,
  onChange,
}: JsonPropertyEditFieldsProps) {
  const fields = useJsonSchemaFields(schema);

  const handleFieldChange = useCallback(
    (key: string, fieldValue: unknown) => {
      const updated = { ...(value ?? {}) };
      if (
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === ''
      ) {
        Reflect.deleteProperty(updated, key);
      } else {
        updated[key] = fieldValue;
      }
      onChange(updated);
    },
    [value, onChange]
  );

  if (fields.length === 0) {
    return null;
  }

  return (
    <>
      {fields.map(field => {
        if (field.refType === 'entity') {
          return (
            <EntityRefEditRow
              key={field.key}
              field={field}
              config={value}
              orgId={orgId}
              sourceEntityTypeName={sourceEntityTypeName}
              sourceEntityId={sourceEntityId}
              onFieldChange={handleFieldChange}
            />
          );
        }
        return (
          <FieldTypeEditRow
            key={field.key}
            field={field}
            config={value}
            entityTypeName={sourceEntityTypeName}
            onFieldChange={handleFieldChange}
          />
        );
      })}
    </>
  );
}
